import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, MapPin, ChefHat, Heart, Truck, ArrowRight,
  Utensils, Package, HandPlatter, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ListingCard } from '@/components/ListingCard';
import { FaqSection } from '@/components/FaqSection';
import { CUISINES } from '@/data/cuisines';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Warm palette (Airbnb/Etsy vibe)
const CREAM_BG = '#FBF6EC';        // page cream
const CREAM_SOFT = '#F4EEDF';      // secondary cream / cards
const CREAM_DEEP = '#E8DFC9';      // separators
const INK = '#2A2320';             // near-black, warmer than pure black
const INK_SOFT = '#6B5F55';        // body copy
const ORANGE = '#D95A41';          // primary CTA (kept, matches existing brand)
const ORANGE_DEEP = '#B84427';     // hover / accent
const SAGE = '#7A8B6F';            // muted green accent
const SAGE_DEEP = '#4F6A4E';       // deeper accent text

// Hero: warm "hands reaching for shared table" — evokes family meal
const HERO_IMAGE = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=2400&q=85';

// Curated popular cuisines for the storytelling strip
const POPULAR_CUISINES = [
  { name: 'Indian',    emoji: '🇮🇳' },
  { name: 'Italian',   emoji: '🇮🇹' },
  { name: 'Thai',      emoji: '🇹🇭' },
  { name: 'Chinese',   emoji: '🇨🇳' },
  { name: 'Lebanese',  emoji: '🇱🇧' },
  { name: 'Sri Lankan',emoji: '🇱🇰' },
  { name: 'Greek',     emoji: '🇬🇷' },
  { name: 'Vietnamese',emoji: '🇻🇳' },
  { name: 'Mexican',   emoji: '🇲🇽' },
  { name: 'Japanese',  emoji: '🇯🇵' },
  { name: 'Filipino',  emoji: '🇵🇭' },
  { name: 'Ethiopian', emoji: '🇪🇹' }
];

const STEPS = [
  {
    icon: Search,
    tint: '#FDEEE7',
    tintText: '#B84427',
    title: 'Discover a home cook',
    body: 'Browse dishes made by real neighbours — from Aunty Priya\'s biryani to Marco\'s Sunday sourdough.'
  },
  {
    icon: HandPlatter,
    tint: '#EBF0E5',
    tintText: '#4F6A4E',
    title: 'Message & agree',
    body: 'Chat directly with the cook, confirm portions, allergies, and pickup or delivery — no middleman.'
  },
  {
    icon: Heart,
    tint: '#F6EBD5',
    tintText: '#8A6A15',
    title: 'Enjoy real home food',
    body: 'Eat something made with time and love. Leave a review. Support a family down the road.'
  }
];

export const HomePage = () => {
  const [listings, setListings] = useState([]);
  const [dish, setDish] = useState('');
  const [cuisine, setCuisine] = useState('');
  const [suburb, setSuburb] = useState('');
  const [fulfillment, setFulfillment] = useState('any'); // any | Pickup | Delivery
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/listings`)
      .then(({ data }) => setListings(data.slice(0, 6)))
      .catch(() => setListings([]));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (dish.trim()) params.append('search', dish.trim());
    if (cuisine) params.append('cuisine', cuisine);
    if (suburb.trim()) params.append('location', suburb.trim());
    if (fulfillment !== 'any') params.append('fulfillment', fulfillment);
    navigate(`/listings${params.toString() ? '?' + params.toString() : ''}`);
  };

  return (
    <div style={{ backgroundColor: CREAM_BG, minHeight: '100vh' }}>
      {/* ================= HERO ================= */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: CREAM_BG }}
        data-testid="hero-section"
      >
        <div className="relative h-[78vh] min-h-[560px] md:min-h-[640px] w-full">
          <img
            src={HERO_IMAGE}
            alt="A family sharing a warm homemade meal around a wooden table"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'saturate(1.05)' }}
          />
          {/* Warm cream vignette so text is readable and stays on-palette */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to bottom, rgba(42,35,32,0.32) 0%, rgba(42,35,32,0.18) 40%, rgba(251,246,236,0.6) 88%, rgba(251,246,236,1) 100%)'
            }}
          />
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 sm:px-8 pb-24 md:pb-36">
            <span
              className="inline-flex items-center gap-2 rounded-full mb-5"
              style={{
                backgroundColor: 'rgba(251, 246, 236, 0.92)',
                color: SAGE_DEEP,
                fontFamily: 'Manrope',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                padding: '8px 16px',
                boxShadow: '0 8px 24px -12px rgba(42,35,32,0.25)'
              }}
              data-testid="hero-eyebrow"
            >
              <Sparkles size={13} strokeWidth={1.75} /> Homemade food • Local love
            </span>

            <h1
              className="text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl"
              style={{
                fontFamily: 'Cormorant Garamond',
                fontWeight: 600,
                color: '#FFFFFF',
                textShadow: '0 4px 24px rgba(42,35,32,0.35)'
              }}
              data-testid="hero-heading"
            >
              Taste the Comfort of Home,<br className="hidden sm:block" /> Anywhere in Australia.
            </h1>

            <p
              className="mt-5 max-w-2xl text-base sm:text-lg md:text-xl"
              style={{
                fontFamily: 'Manrope',
                color: 'rgba(255,255,255,0.94)',
                textShadow: '0 2px 12px rgba(42,35,32,0.4)',
                lineHeight: 1.6
              }}
              data-testid="hero-subhead"
            >
              Warm dinners, family recipes, and Sunday bakes — cooked by the neighbours in your suburb.
            </p>
          </div>
        </div>

        {/* Search widget — overlaps the hero image, Airbnb-style */}
        <div className="relative -mt-24 md:-mt-32 px-4 sm:px-6 lg:px-8 pb-8 z-20">
          <form
            onSubmit={handleSearch}
            className="max-w-5xl mx-auto rounded-3xl p-3 sm:p-4"
            style={{
              backgroundColor: '#FFFFFF',
              boxShadow: '0 30px 80px -30px rgba(42,35,32,0.35), 0 8px 24px -12px rgba(42,35,32,0.2)'
            }}
            data-testid="hero-search-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr_1fr_1.25fr] gap-2 md:gap-1">
              {/* Dish */}
              <label className="flex items-center gap-3 rounded-2xl px-4 py-3 md:py-4 cursor-text transition-colors hover:bg-[#FBF6EC]">
                <Search size={18} strokeWidth={1.5} style={{ color: ORANGE, flexShrink: 0 }} />
                <div className="min-w-0 flex-1">
                  <div className="uppercase" style={{ fontFamily: 'Manrope', fontSize: '10px', fontWeight: 700, letterSpacing: '0.14em', color: INK_SOFT }}>
                    Dish
                  </div>
                  <input
                    type="text"
                    value={dish}
                    onChange={(e) => setDish(e.target.value)}
                    placeholder="What are you craving today?"
                    className="w-full bg-transparent focus:outline-none placeholder:text-[#B8A99A]"
                    style={{ fontFamily: 'Manrope', fontSize: '14px', color: INK }}
                    data-testid="search-dish-input"
                  />
                </div>
              </label>

              {/* Cuisine */}
              <label className="flex items-center gap-3 rounded-2xl px-4 py-3 md:py-4 cursor-pointer transition-colors hover:bg-[#FBF6EC] md:border-l md:border-[#E8DFC9]">
                <Utensils size={18} strokeWidth={1.5} style={{ color: SAGE_DEEP, flexShrink: 0 }} />
                <div className="min-w-0 flex-1">
                  <div className="uppercase" style={{ fontFamily: 'Manrope', fontSize: '10px', fontWeight: 700, letterSpacing: '0.14em', color: INK_SOFT }}>
                    Cuisine
                  </div>
                  <select
                    value={cuisine}
                    onChange={(e) => setCuisine(e.target.value)}
                    className="w-full bg-transparent focus:outline-none appearance-none"
                    style={{ fontFamily: 'Manrope', fontSize: '14px', color: cuisine ? INK : '#B8A99A' }}
                    data-testid="search-cuisine-select"
                  >
                    <option value="">Any cuisine</option>
                    {CUISINES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </label>

              {/* Suburb */}
              <label className="flex items-center gap-3 rounded-2xl px-4 py-3 md:py-4 cursor-text transition-colors hover:bg-[#FBF6EC] md:border-l md:border-[#E8DFC9]">
                <MapPin size={18} strokeWidth={1.5} style={{ color: '#8A6A15', flexShrink: 0 }} />
                <div className="min-w-0 flex-1">
                  <div className="uppercase" style={{ fontFamily: 'Manrope', fontSize: '10px', fontWeight: 700, letterSpacing: '0.14em', color: INK_SOFT }}>
                    Suburb
                  </div>
                  <input
                    type="text"
                    value={suburb}
                    onChange={(e) => setSuburb(e.target.value)}
                    placeholder="e.g. Parramatta"
                    className="w-full bg-transparent focus:outline-none placeholder:text-[#B8A99A]"
                    style={{ fontFamily: 'Manrope', fontSize: '14px', color: INK }}
                    data-testid="search-suburb-input"
                  />
                </div>
              </label>

              {/* Fulfillment toggle */}
              <div className="flex items-center gap-2 rounded-2xl px-3 py-2 md:py-2 md:border-l md:border-[#E8DFC9]">
                <div className="flex items-center gap-3 flex-1 min-w-0 pl-1">
                  <Truck size={18} strokeWidth={1.5} style={{ color: INK_SOFT, flexShrink: 0 }} />
                  <div className="min-w-0 flex-1">
                    <div className="uppercase" style={{ fontFamily: 'Manrope', fontSize: '10px', fontWeight: 700, letterSpacing: '0.14em', color: INK_SOFT }}>
                      How
                    </div>
                    <div className="flex gap-1 mt-0.5 flex-wrap" data-testid="search-fulfillment-toggle">
                      {['any', 'Pickup', 'Delivery'].map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setFulfillment(v)}
                          className="px-2 py-0.5 rounded-full transition-colors"
                          style={{
                            fontFamily: 'Manrope',
                            fontSize: '11px',
                            fontWeight: 600,
                            backgroundColor: fulfillment === v ? SAGE_DEEP : 'transparent',
                            color: fulfillment === v ? '#FFFFFF' : INK,
                            border: fulfillment === v ? '1px solid transparent' : '1px solid #E8DFC9'
                          }}
                          data-testid={`fulfillment-${v.toLowerCase()}`}
                        >
                          {v === 'any' ? 'Any' : v}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="rounded-2xl h-12 w-12 md:h-14 md:w-14 flex-shrink-0"
                  style={{
                    backgroundColor: ORANGE,
                    boxShadow: '0 12px 24px -12px rgba(184,68,39,0.6)'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = ORANGE_DEEP)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = ORANGE)}
                  aria-label="Search dishes"
                  data-testid="hero-search-btn"
                >
                  <Search size={20} strokeWidth={2} color="#FFFFFF" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-16 pb-16" data-testid="how-it-works">
        <div className="max-w-2xl mb-10 md:mb-14">
          <p
            className="uppercase mb-3"
            style={{ fontFamily: 'Manrope', fontSize: '12px', fontWeight: 600, letterSpacing: '0.18em', color: ORANGE }}
          >
            The way home food should work
          </p>
          <h2
            className="text-3xl md:text-5xl leading-tight"
            style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: INK }}
          >
            Three steps to a meal that tastes like family.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5 md:gap-8">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                className="rounded-3xl p-6 md:p-8 relative overflow-hidden"
                style={{ backgroundColor: '#FFFFFF', boxShadow: '0 20px 40px -30px rgba(42,35,32,0.18)' }}
                data-testid={`step-${i + 1}`}
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: s.tint, color: s.tintText }}
                >
                  <Icon size={22} strokeWidth={1.5} />
                </div>
                <div className="uppercase" style={{ fontFamily: 'Manrope', fontSize: '11px', fontWeight: 700, letterSpacing: '0.16em', color: INK_SOFT }}>
                  Step {i + 1}
                </div>
                <h3
                  className="text-2xl md:text-3xl mt-1 mb-3 leading-tight"
                  style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: INK }}
                >
                  {s.title}
                </h3>
                <p style={{ fontFamily: 'Manrope', color: INK_SOFT, lineHeight: 1.7, fontSize: '15px' }}>
                  {s.body}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ================= POPULAR CUISINES ================= */}
      <section
        className="py-14 md:py-20"
        style={{ backgroundColor: CREAM_SOFT }}
        data-testid="popular-cuisines"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 md:mb-10 flex items-end justify-between gap-4">
          <div>
            <p
              className="uppercase mb-2"
              style={{ fontFamily: 'Manrope', fontSize: '12px', fontWeight: 600, letterSpacing: '0.18em', color: SAGE_DEEP }}
            >
              A world in your suburb
            </p>
            <h2
              className="text-3xl md:text-5xl leading-tight"
              style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: INK }}
            >
              Cuisines that travel to your table.
            </h2>
          </div>
          <Link
            to="/listings"
            className="hidden md:inline-flex items-center gap-1 group"
            style={{ fontFamily: 'Manrope', fontWeight: 600, color: ORANGE }}
          >
            See all dishes <ArrowRight size={16} strokeWidth={2} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-3 md:gap-4 px-4 sm:px-6 lg:px-8 pb-2 min-w-max">
            {POPULAR_CUISINES.map((c) => (
              <Link
                key={c.name}
                to={`/listings?cuisine=${encodeURIComponent(c.name)}`}
                className="flex-shrink-0 group"
                data-testid={`popular-cuisine-${c.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div
                  className="rounded-2xl px-5 py-4 flex items-center gap-3 transition-transform group-hover:-translate-y-0.5"
                  style={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 8px 20px -14px rgba(42,35,32,0.2)',
                    minWidth: '190px'
                  }}
                >
                  <span style={{ fontSize: '28px', lineHeight: 1 }} aria-hidden="true">{c.emoji}</span>
                  <div className="min-w-0">
                    <div style={{ fontFamily: 'Manrope', fontWeight: 600, color: INK, fontSize: '15px' }} className="truncate">
                      {c.name}
                    </div>
                    <div style={{ fontFamily: 'Manrope', fontSize: '12px', color: INK_SOFT }}>
                      Explore dishes
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FEATURED DISHES ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="featured-section">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <p
              className="uppercase mb-2"
              style={{ fontFamily: 'Manrope', fontSize: '12px', fontWeight: 600, letterSpacing: '0.18em', color: ORANGE }}
            >
              Fresh from the kitchen
            </p>
            <h2
              className="text-3xl md:text-5xl leading-tight"
              style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: INK }}
              data-testid="featured-heading"
            >
              Made today by cooks near you.
            </h2>
          </div>
          <Link to="/listings">
            <Button
              variant="ghost"
              className="rounded-full"
              style={{ fontFamily: 'Manrope', fontWeight: 600, color: ORANGE }}
              data-testid="view-all-btn"
            >
              Browse all dishes <ArrowRight size={16} strokeWidth={2} className="ml-1" />
            </Button>
          </Link>
        </div>

        {listings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
            {listings.map((listing) => (
              <ListingCard key={listing.id} listing={listing} showDietaryTags={false} />
            ))}
          </div>
        ) : (
          <div className="rounded-3xl py-16 text-center" style={{ backgroundColor: '#FFFFFF' }}>
            <Utensils size={32} strokeWidth={1.2} style={{ color: '#B8A99A' }} className="mx-auto mb-3" />
            <p style={{ fontFamily: 'Manrope', color: INK_SOFT, marginBottom: '16px' }}>
              No dishes yet. Be the first cook in your neighbourhood.
            </p>
            <Link to="/post">
              <Button className="rounded-full px-6" style={{ backgroundColor: ORANGE, fontFamily: 'Manrope', fontWeight: 600 }}>
                Post the first meal
              </Button>
            </Link>
          </div>
        )}
      </section>

      {/* ================= BECOME A COOK CTA ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24" data-testid="become-a-cook-cta">
        <div
          className="rounded-3xl overflow-hidden grid md:grid-cols-2 items-stretch"
          style={{
            backgroundColor: SAGE_DEEP,
            boxShadow: '0 40px 80px -40px rgba(79,106,78,0.55)'
          }}
        >
          <div className="p-8 sm:p-12 lg:p-16 flex flex-col justify-center">
            <div
              className="inline-flex items-center gap-2 self-start rounded-full mb-4 sm:mb-6"
              style={{
                backgroundColor: 'rgba(251,246,236,0.15)',
                padding: '6px 14px',
                fontFamily: 'Manrope',
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.14em',
                color: '#FBF6EC',
                textTransform: 'uppercase'
              }}
            >
              <ChefHat size={13} strokeWidth={1.75} /> Home cooks welcome
            </div>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl leading-tight mb-4"
              style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#FBF6EC' }}
            >
              Your grandma's recipe deserves a bigger table.
            </h2>
            <p
              className="text-base md:text-lg mb-8 max-w-lg"
              style={{ fontFamily: 'Manrope', color: 'rgba(251,246,236,0.85)', lineHeight: 1.7 }}
            >
              Turn the meals your family loves into a small business. Set your own prices, cook when it suits you,
              and build a following in your suburb. We handle discovery — you handle the flavour.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/register">
                <Button
                  className="rounded-full h-12 px-8 w-full sm:w-auto"
                  style={{ backgroundColor: ORANGE, fontFamily: 'Manrope', fontWeight: 700, fontSize: '15px' }}
                  data-testid="become-cook-btn"
                >
                  Become a cook <ArrowRight size={16} strokeWidth={2} className="ml-1" />
                </Button>
              </Link>
              <Link to="/blog/how-to-start-selling-home-cooked-food">
                <Button
                  variant="outline"
                  className="rounded-full h-12 px-6 w-full sm:w-auto bg-transparent"
                  style={{
                    borderColor: 'rgba(251,246,236,0.4)',
                    color: '#FBF6EC',
                    fontFamily: 'Manrope',
                    fontWeight: 600
                  }}
                  data-testid="cook-guide-btn"
                >
                  Read the beginner's guide
                </Button>
              </Link>
            </div>
          </div>
          <div
            className="relative min-h-[280px] md:min-h-[420px]"
            style={{
              backgroundImage:
                'url(https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&w=1600&q=85)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
            aria-hidden="true"
          >
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(135deg, rgba(79,106,78,0.65) 0%, rgba(79,106,78,0.15) 40%, rgba(79,106,78,0) 100%)'
              }}
            />
          </div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <FaqSection />
    </div>
  );
};
